import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Alert,
  Tooltip,
} from "antd";
import {
  InfoCircleOutlined,
  DollarOutlined,
  PercentageOutlined,
  FunctionOutlined,
} from "@ant-design/icons";
import type { HRPayrollItem, PayrollItemFormData } from "../../types/hr";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface PayrollItemFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: PayrollItemFormData) => void;
  initialValues?: HRPayrollItem | null;
  isEditing: boolean;
}

const PayrollItemForm: React.FC<PayrollItemFormProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  isEditing,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue({
          code: initialValues.code,
          name: initialValues.name,
          custom_description: initialValues.custom_description,
          type: initialValues.type,
          category: initialValues.category,
          calculation_type: initialValues.calculation_type,
          base_amount: initialValues.base_amount,
          percentage: initialValues.percentage,
          formula: initialValues.formula,
          min_amount: initialValues.min_amount,
          max_amount: initialValues.max_amount,
          subject_to_tax: initialValues.subject_to_tax,
          subject_to_cnss: initialValues.subject_to_cnss,
          subject_to_tpa: initialValues.subject_to_tpa,
          display_order: initialValues.display_order,
          is_active: initialValues.is_active,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          type: "gain",
          category: "allowance",
          calculation_type: "fixed",
          base_amount: 0,
          percentage: 0,
          min_amount: 0,
          max_amount: 999999999,
          subject_to_tax: true,
          subject_to_cnss: true,
          subject_to_tpa: true,
          display_order: 0,
          is_active: true,
        });
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = (values: PayrollItemFormData) => {
    const standardFormulas: Record<string, string> = {
      HS_15: 'OVERTIME_CALC(overtime_hours_15, 1.15, base_salary)',
      HS_35: 'OVERTIME_CALC(overtime_hours_35, 1.35, base_salary)',
    };
    onSubmit({ ...values, ...(values.calculation_type === 'formula'
      ? { formula: values.formula?.trim() || standardFormulas[values.code] } : {}) });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const calculationType = Form.useWatch("calculation_type", form);
  const itemType = Form.useWatch("type", form);
  const category = Form.useWatch("category", form);

  const getCalculationHelp = () => {
    switch (calculationType) {
      case "fixed":
        return "Montant fixe en FCFA";
      case "percentage":
        return "Pourcentage du salaire de base";
      case "formula":
        return "Formule de calcul personnalisée";
      default:
        return "";
    }
  };

  const shouldShowCustomDescription = () => {
    return (
      category === "allowance" ||
      category === "bonus" ||
      category === "deduction"
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DollarOutlined />
          {isEditing ? "Modifier l'élément de paie" : "Nouvel élément de paie"}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: "gain",
          category: "allowance",
          calculation_type: "fixed",
          base_amount: 0,
          percentage: 0,
          min_amount: 0,
          max_amount: 999999999,
          subject_to_tax: true,
          subject_to_cnss: true,
          subject_to_tpa: true,
          display_order: 0,
          is_active: true,
        }}
      >
        {/* Informations de base */}
        <Card
          title="Informations générales"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Code"
                name="code"
                rules={[
                  { required: true, message: "Le code est obligatoire" },
                  {
                    pattern: /^[A-Z0-9_]+$/,
                    message: "Code invalide (A-Z, 0-9, _)",
                  },
                ]}
              >
                <Input placeholder="Ex: IND_TRANS" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: "Le nom est obligatoire" }]}
              >
                <Input placeholder="Ex: Indemnité de transport" />
              </Form.Item>
            </Col>
          </Row>

          {shouldShowCustomDescription() && (
            <Form.Item
              label="Description personnalisée"
              name="custom_description"
              extra="Utilisée pour les 'Autres indemnités' pour préciser la nature"
            >
              <TextArea
                rows={2}
                placeholder="Ex: Prime de performance trimestrielle"
                maxLength={255}
              />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: "Le type est obligatoire" }]}
              >
                <Select>
                  <Option value="gain">
                    <span style={{ color: "#52c41a" }}>Gain</span>
                  </Option>
                  <Option value="deduction">
                    <span style={{ color: "#ff4d4f" }}>Déduction</span>
                  </Option>
                  <Option value="info">
                    <span style={{ color: "#1890ff" }}>Information</span>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Catégorie"
                name="category"
                rules={[
                  { required: true, message: "La catégorie est obligatoire" },
                ]}
              >
                <Select>
                  <Option value="salary">Salaire</Option>
                  <Option value="allowance">Indemnité</Option>
                  <Option value="bonus">Prime</Option>
                  <Option value="deduction">Déduction</Option>
                  <Option value="tax">Impôt</Option>
                  <Option value="social_contribution">
                    Cotisation sociale
                  </Option>
                  <Option value="loan">Prêt</Option>
                  <Option value="advance">Avance</Option>
                  <Option value="info">Information</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Ordre d'affichage"
                name="display_order"
                extra="Ordre dans les bulletins de paie"
              >
                <InputNumber
                  min={0}
                  max={999}
                  style={{ width: "100%" }}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Calcul */}
        <Card
          title="Méthode de calcul"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Form.Item
            label="Type de calcul"
            name="calculation_type"
            rules={[
              { required: true, message: "Le type de calcul est obligatoire" },
            ]}
          >
            <Select>
              <Option value="fixed">
                <DollarOutlined /> Montant fixe
              </Option>
              <Option value="percentage">
                <PercentageOutlined /> Pourcentage
              </Option>
              <Option value="formula">
                <FunctionOutlined /> Formule
              </Option>
            </Select>
          </Form.Item>

          <Alert
            message={getCalculationHelp()}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            {calculationType === "fixed" && (
              <Col span={8}>
                <Form.Item label="Montant de base (FCFA)" name="base_amount">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                    }
                  />
                </Form.Item>
              </Col>
            )}

            {calculationType === "percentage" && (
              <Col span={8}>
                <Form.Item label="Pourcentage (%)" name="percentage">
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    style={{ width: "100%" }}
                    addonAfter="%"
                  />
                </Form.Item>
              </Col>
            )}

            {calculationType === "formula" && (
              <Col span={16}>
                <Form.Item
                  label="Formule"
                  name="formula"
                  rules={[({ getFieldValue }) => ({ validator: (_, value) =>
                    String(value || '').trim() || ['HS_15', 'HS_35'].includes(getFieldValue('code'))
                      ? Promise.resolve() : Promise.reject(new Error('Une formule est requise pour cette rubrique')) })]}
                  extra="Formule de calcul personnalisée (ex: base_salary * 0.15)"
                >
                  <TextArea
                    rows={2}
                    placeholder="Ex: (base_salary / 173.33) * overtime_hours * 1.5"
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={8}>
              <Form.Item label="Montant minimum" name="min_amount">
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Montant maximum" name="max_amount">
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Paramètres fiscaux */}
        {itemType === "gain" && (
          <Card
            title="Paramètres fiscaux et sociaux"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Alert
              message="Ces paramètres déterminent si l'élément est soumis aux taxes et cotisations"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label={
                    <span>
                      Soumis à l'IRPP{" "}
                      <Tooltip title="Impôt sur le Revenu des Personnes Physiques">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  name="subject_to_tax"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={
                    <span>
                      Soumis à la CNSS{" "}
                      <Tooltip title="Caisse Nationale de Sécurité Sociale">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  name="subject_to_cnss"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={
                    <span>
                      Soumis à la TPA{" "}
                      <Tooltip title="Taxe Patronale d'Apprentissage">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  name="subject_to_tpa"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {/* Statut */}
        <Card title="Statut" size="small">
          <Form.Item
            label="Élément actif"
            name="is_active"
            valuePropName="checked"
            extra="Seuls les éléments actifs apparaissent dans les calculs de paie"
          >
            <Switch />
          </Form.Item>
        </Card>
      </Form>
    </Modal>
  );
};

export default PayrollItemForm;
